import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useCart } from '../hooks/useCart'
import * as db from '../services/db'
import { playCheckoutSound } from '../services/sounds'
import { hapticLight, hapticSuccess } from '../utils/haptic'
import { generateId } from '../utils/uuid'

const CELEBRATION_KEY = 'celebrationEnabled'
const CASH_LEARNING_KEY = 'cashLearningMode'

const BILLS = [
  { label: '$20', valueCents: 2000 },
  { label: '$10', valueCents: 1000 },
  { label: '$5', valueCents: 500 },
  { label: '$1', valueCents: 100 },
] as const

const COINS = [
  { label: '25¢', name: 'quarter', valueCents: 25 },
  { label: '10¢', name: 'dime', valueCents: 10 },
  { label: '5¢', name: 'nickel', valueCents: 5 },
  { label: '1¢', name: 'penny', valueCents: 1 },
] as const

function changeInWords(changeCents: number): string {
  if (changeCents <= 0) return ''
  const dollars = Math.floor(changeCents / 100)
  const cents = changeCents % 100
  const parts: string[] = []
  if (dollars > 0) parts.push(dollars === 1 ? '1 dollar' : `${dollars} dollars`)
  if (cents > 0) parts.push(cents === 1 ? '1 cent' : `${cents} cents`)
  return parts.join(' and ')
}

export function PaymentView() {
  const navigate = useNavigate()
  const { cart, subtotal, clear } = useCart()
  const [payType, setPayType] = useState<'cash' | 'card'>('cash')
  const [processing, setProcessing] = useState(false)
  const [celebrationEnabled, setCelebrationEnabled] = useState(true)
  const [cashLearningMode, setCashLearningMode] = useState(true)
  const [cashGivenCents, setCashGivenCents] = useState(0)

  const totalCents = Math.round(subtotal * 100)
  const changeCents = cashGivenCents >= totalCents ? cashGivenCents - totalCents : 0
  const canCompleteCash = cashGivenCents >= totalCents

  useEffect(() => {
    db.getSetting(CELEBRATION_KEY).then((v) => setCelebrationEnabled(v !== 'false'))
    db.getSetting(CASH_LEARNING_KEY).then((v) => setCashLearningMode(v !== 'false'))
  }, [])

  useEffect(() => {
    if (payType === 'card') setCashGivenCents(0)
  }, [payType])

  const addCash = (cents: number) => {
    hapticLight()
    setCashGivenCents((c) => c + cents)
  }

  const handlePay = async () => {
    setProcessing(true)
    playCheckoutSound()
    hapticSuccess()
    const receipt = {
      id: generateId(),
      items: [...cart],
      total: subtotal,
      timestamp: Date.now(),
      paymentType: payType,
    }
    try {
      await db.addReceipt(receipt)
      await clear()
    } finally {
      setProcessing(false)
    }

    if (payType === 'cash' && cashLearningMode && canCompleteCash) {
      navigate('/payment/change', {
        state: { receiptId: receipt.id, changeCents },
      })
      return
    }

    if (payType === 'card' || (payType === 'cash' && !cashLearningMode)) {
      setTimeout(() => navigate(`/receipt/${receipt.id}`), 1200)
      if (celebrationEnabled) {
        import('canvas-confetti')
          .then((mod) => {
            try {
              mod.default({ particleCount: 120, spread: 80, origin: { y: 0.6 } })
            } catch {
              // ignore
            }
          })
          .catch(() => {})
      }
    }
  }

  if (cart.length === 0) {
    return (
      <div className="page page--payment">
        <div className="empty-state">
          <span className="empty-state-emoji" aria-hidden>🛒</span>
          <h2 className="empty-state-title">Your cart is empty</h2>
          <p className="empty-state-p">Add items from the home screen or scan to get started.</p>
          <Link to="/grocery" className="btn-large btn-primary" style={{ textDecoration: 'none' }}>
            Go shopping
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="page page--payment">
      <h1 className="page-title">💳 Payment</h1>
      <div className="payment-total-card">
        <p className="payment-total-label">Total to pay</p>
        <p className="payment-total-amount">${subtotal.toFixed(2)}</p>
      </div>
      <p className="payment-method-label">How are you paying?</p>
      <div className="payment-method-row">
        <button
          type="button"
          className={`btn-large payment-method-btn ${payType === 'cash' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setPayType('cash')}
          aria-pressed={payType === 'cash'}
          aria-label="Pay with cash"
        >
          💵 Cash
        </button>
        <button
          type="button"
          className={`btn-large payment-method-btn ${payType === 'card' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setPayType('card')}
          aria-pressed={payType === 'card'}
          aria-label="Pay with card"
        >
          💳 Card
        </button>
      </div>

      {payType === 'cash' && (
        <div className="payment-cash-mode-toggle">
          <span className="payment-cash-mode-label">Cash:</span>
          <button
            type="button"
            className={`payment-cash-mode-btn ${!cashLearningMode ? 'payment-cash-mode-btn--on' : ''}`}
            onClick={async () => {
              setCashLearningMode(false)
              await db.setSetting(CASH_LEARNING_KEY, 'false')
            }}
            aria-pressed={!cashLearningMode}
          >
            Simple
          </button>
          <button
            type="button"
            className={`payment-cash-mode-btn ${cashLearningMode ? 'payment-cash-mode-btn--on' : ''}`}
            onClick={async () => {
              setCashLearningMode(true)
              await db.setSetting(CASH_LEARNING_KEY, 'true')
            }}
            aria-pressed={cashLearningMode}
          >
            Learning
          </button>
        </div>
      )}

      {payType === 'cash' && cashLearningMode ? (
        <div className="payment-cash-learning">
          <p className="payment-cash-instruction">
            Tap the bills and coins you give the cashier. Add enough to pay the total!
          </p>
          <div className="payment-cash-money">
            <p className="payment-cash-section-label">Bills</p>
            <div className="payment-cash-row">
              {BILLS.map((b) => (
                <button
                  key={b.label}
                  type="button"
                  className="payment-cash-coin-btn payment-cash-bill"
                  onClick={() => addCash(b.valueCents)}
                  aria-label={`Add ${b.label}`}
                >
                  {b.label}
                </button>
              ))}
            </div>
            <p className="payment-cash-section-label">Coins</p>
            <div className="payment-cash-row">
              {COINS.map((c) => (
                <button
                  key={c.label}
                  type="button"
                  className="payment-cash-coin-btn payment-cash-coin"
                  onClick={() => addCash(c.valueCents)}
                  aria-label={`Add ${c.name}, ${c.label}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
          <div className="payment-cash-given-card">
            <p className="payment-cash-given-label">You&apos;ve given</p>
            <p className="payment-cash-given-amount">${(cashGivenCents / 100).toFixed(2)}</p>
          </div>
          {canCompleteCash && (
            <div className="payment-cash-change-card">
              <p className="payment-cash-change-label">Change back</p>
              <p className="payment-cash-change-amount">${(changeCents / 100).toFixed(2)}</p>
              <p className="payment-cash-change-words">{changeInWords(changeCents)}</p>
            </div>
          )}
          <button
            type="button"
            className="btn-large btn-primary payment-cash-complete"
            onClick={handlePay}
            disabled={processing || !canCompleteCash}
            aria-busy={processing}
          >
            {processing ? 'Processing…' : 'Complete purchase'}
          </button>
        </div>
      ) : payType === 'cash' && !cashLearningMode ? (
        <button
          type="button"
          className="btn-large btn-primary"
          onClick={handlePay}
          disabled={processing}
          aria-busy={processing}
        >
          {processing ? 'Processing…' : 'Complete purchase'}
        </button>
      ) : (
        <button
          type="button"
          className="btn-large btn-primary"
          onClick={handlePay}
          disabled={processing}
          aria-busy={processing}
        >
          {processing ? 'Processing…' : 'Complete purchase'}
        </button>
      )}

      <Link
        to="/cart"
        className="payment-back-link"
      >
        ← Back to cart
      </Link>
    </div>
  )
}
